/* eslint-disable prettier/prettier */
import CryptoJS from 'crypto-js';
import Base32 from 'hi-base32-custom';

// Good references for password protection:
// https://crackstation.net/hashing-security.htm

// From this website, good slow hash functions:
// - PBKDF2
// - bcrypt

// DO use:
// Well - designed key stretching algorithms such as PBKDF2, bcrypt, and scrypt.
//  OpenWall's Portable PHP password hashing framework
// My implementations of PBKDF2 in PHP, C#, Java, and Ruby.
// Secure versions of crypt($2y$, $5$, $6$)

// DO NOT use:
// Fast cryptographic hash functions such as MD5, SHA1, SHA256, SHA512, RipeMD, WHIRLPOOL, SHA3, etc.
// Insecure versions of crypt($1$, $2$, $2x$, $3$).
// Any algorithm that you designed yourself.Only use technology that is in the public domain and has been well - tested by experienced cryptographers.

// Even though there are no cryptographic attacks on MD5 or SHA1 that make their hashes easier to crack,
// they are old and are widely considered (somewhat incorrectly) to be inadequate for password storage.So I don't recommend using them.
// An exception to this rule is PBKDF2, which is frequently implemented using SHA1 as the underlying hash function.

// Good website for testing hashing and encryption:
// https://gchq.github.io/CyberChef/

// Javascript encryption
// https://dev.to/halan/4-ways-of-symmetric-cryptography-and-javascript-how-to-aes-with-javascript-3o1b
// http://crypto.stanford.edu/sjcl/
// https://www.npmjs.com/package/crypto-js

// On purpose, we store the key in the client code, and not from the backend. If the backend is
// attacked, the attacker will have to also attack the client code to be able to figure out the
// actual key. Storing both fixed keys and salts in the backend is making it too easy for an
// attacker. And in any case, an attacker on the client could also figure out user.encryption_key
// and user.encryption_iv. We also want to decrypt while there is no user.
// TODO: Improve this further. Leverage versioning in the encrypted payload itself.
// TODO: Encrypt on both client and backend, but with different keys.
// TODO: Verify in the backend that key fields are encrypted by the client

// Consider using https://jsfuck.com to obfuscate the key.
const a0 = 'jE3LDI0NywxODUsMsOTIsMjIyLDE1NywNywyMTAsMjM0LDE4';
const a0Prefix = '|A0|'; // Backend will use a different prefix so it will be clear if an encryption is incorrect

// TODO: Use |B0| for backend --> What should be stored in the DB
// TODO: Use |C0| for client --> What should be sent and received by the client
// Eventually we won't have |A0| fields anymore, or we could upgrade them over time.

export function encryptOnClient(text) {
  if (text.startsWith(a0Prefix)) {
    // If somehow, this was already encrypted, do not encrypt it again
    // TODO: Report this case as this would be a bug
    return text;
  }
  // We do not pass an IV, which will generate a new salt every time (as shown in the unit-tests).
  // We prefix with '|A0|' to indicate the encryption implementation and key.
  return `${a0Prefix}${CryptoJS.AES.encrypt(text, a0).toString()}`;
}

export function decryptOnClient(text) {
  if (text?.startsWith(a0Prefix)) {
    // 4 for length of '|A0|'
    return CryptoJS.AES.decrypt(text.substring(4), a0).toString(CryptoJS.enc.Utf8);
  }
  return text;
}

// TBD if we want to hash the email/password only on the client, or also on the backend
// Checking the pair email/password on the server will improve the security (both have to be true to validate the user),
// so a hacker cannot check if an email exists (first step before trying to hack the password, or try a leaked password).

// We expect multiple versions for the encryption algorithms over time. Usually more secure, sometimes faster.
// Also a variation of algorithms make the life harder on hackers, as they cannot use standard dictionaries.

// Algorithm versions:
//  Each algorithm will be versionned, and a sinle integer will aggregate all versions through a hex mask. (0xAABBCCDD)
//
//  CompoundKey Version:
//    Example:
//      (['foo', 'bar'], 'salt') ==> 'salt_foo_bar'
//    We expect few iterations often on this one. Most other implementations can leverage this V0 directly.
//    We don't expect code to directly interact with this.
//    Version mask 0xFF000000
//
//  Hash Version:
//    Example:
//      (['foo', 'bar'], 'salt') ==> SHA256('salt_foo_bar')
//    We expect many iterations on this one.
//    Version mask: 0x00FF0000
//
//  Encryption Version:
//    Example:
//      {JSON: 'blob'} ==> AES({JSON: 'blob'})
//    We expect many iterations in this one.
//    Version mask: 0x0000FF00
//
//  Verification Code Version:
//    Example:
//      SHA256('salt_foo_bar') ==> TBD('salt_foo_bar')
//    We expect only few iterations of this one.
//    Version mask: 0x000000FF
//

function generateSaltedCompoundKeyV0(compoundKeys: string[], salt: string): String {
  return salt.concat('_', compoundKeys.join('_'));
}

export function generateSaltedCompoundKey(compoundKeys: string[], salt: string, compoundKeyVersion: number): string {
  switch (compoundKeyVersion) {
    case 0:
    default:
      return generateSaltedCompoundKeyV0(compoundKeys, salt);
  }
}

export function getSupportedCompoundKeyVersions() {
  return {
    supported: [0],
    recommended: 0,
    deprecated: [],
  };
}

function generateHashedEncryptionKeyV0(saltedCompoundKey: string): string {
  // https://gchq.github.io/CyberChef/#recipe=SHA2('256',64,160)&input=c2FsdF9mb29fYmFy
  // Equivalent of 64 rounds of SHA2 256 bits
  return CryptoJS.SHA256(saltedCompoundKey).toString();
}

export function generateHashedEncryptionKey(saltedCompoundKey: string, hashVersion: number): string {
  switch (hashVersion) {
    case 0:
    default:
      return generateHashedEncryptionKeyV0(saltedCompoundKey);
  }
}

export function getSupportedHashVersions() {
  return {
    supported: [0],
    recommended: 0,
    deprecated: [],
  };
}

function splice(str, index, count) {
  // We cannot pass negative indexes directly to the 2nd slicing operation.
  let sliceIndex = index;
  if (sliceIndex < 0) {
    sliceIndex = str.length + sliceIndex;
    if (sliceIndex < 0) {
      sliceIndex = 0;
    }
  }
  return str.slice(0, sliceIndex) + str.slice(sliceIndex + count);
}

function generateVerificationCodeV0(encryptionKey: string): string {
  // We want to make the logic of the verification code as simple as possible, while not leaking any key information.
  // The original salted encryption key would be a bad choice as it is too close to the source key.
  // The encryption key is a better choice, as it went through a bunch of hashing already.
  // However, it is the encryption key, so if one could reverse engineer it, they could decrypt the payload.

  // So let's shuffle things a bit, so it would be too costly to reverse engineer/brute force it.
  // 1. Remove 32 characters at position 7
  // 2. Replace characters 'a' => '2' and '5' => 'f'
  // 3. 64 rounds of SHA2 256 bits hashing

  let obfuscatedVerificationCode = splice(encryptionKey, 7, 32);
  obfuscatedVerificationCode = obfuscatedVerificationCode.replace(/a/g, '2').replace(/5/g, 'f');
  const fullEncryptionCode = CryptoJS.SHA256(obfuscatedVerificationCode).toString();

  // Limit it to 40 bits, so 10 hex characters. A future version will XOR each 40 bits range, but
  // this is good enough for the moment.
  const strippedEncryptionCode = fullEncryptionCode.slice(0, 10);

  // This loop is sloppy, improve this.
  const bytes = [];
  for (let i = 0; i < strippedEncryptionCode.length; i += 2) {
    bytes.push(parseInt(strippedEncryptionCode.slice(i, i + 2), 16));
  }

  const verificationCode = Base32.encode(bytes);
  if (verificationCode.length === 8) {
    // This is the expected size, but we are being paranoid here
    // Add the dash in the middle of the verification code.
    return verificationCode.slice(0, 4).concat('-', verificationCode.slice(4));
  }
  return verificationCode;
}

export function generateVerificationCode(encryptionKey: string, verificationCodeVersion: number): string {
  switch (verificationCodeVersion) {
    case 0:
    default:
      return generateVerificationCodeV0(encryptionKey);
  }
}

export function getSupportedVerificationCodeVersions() {
  return {
    supported: [0],
    recommended: 0,
    deprecated: [],
  };
}

const encryptV0Salt = '0123456789abcdef';
const encryptV0IV = '1122334455667788';

function encryptPayloadV0(payload: string, encryptionKey: string): string {
  const salt = CryptoJS.enc.Utf8.parse(encryptV0Salt);
  const iv = CryptoJS.enc.Utf8.parse(encryptV0IV);
  const key256Bits = CryptoJS.PBKDF2(encryptionKey, salt, { keySize: 256 / 32, iterations: 1 });
  return CryptoJS.AES.encrypt(payload, key256Bits, { iv }).toString();
}

function encryptPayloadV1(payload: string, encryptionKey: string): string {
  return CryptoJS.AES.encrypt(payload, encryptionKey).toString();
}

function decryptPayloadV0(encryptedPayload: string, encryptionKey: string): string {
  const salt = CryptoJS.enc.Utf8.parse(encryptV0Salt);
  const iv = CryptoJS.enc.Utf8.parse(encryptV0IV);
  const key256Bits = CryptoJS.PBKDF2(encryptionKey, salt, { keySize: 256 / 32 });
  return CryptoJS.AES.decrypt(encryptedPayload, key256Bits, { iv }).toString(CryptoJS.enc.Utf8);
}

function decryptPayloadV1(encryptedPayload: string, encryptionKey: string): string {
  return CryptoJS.AES.decrypt(encryptedPayload, encryptionKey).toString(CryptoJS.enc.Utf8);
}

export function encryptPayload(payload: string, encryptionKey: string, encryptionVersion: number): string {
  switch (encryptionVersion) {
    case 0:
      return encryptPayloadV0(payload, encryptionKey);
    default:
    case 1:
      return encryptPayloadV1(payload, encryptionKey);
  }
}

export function decryptPayload(encryptedPayload: string, encryptionKey: string, encryptionVersion: number): string {
  try {
    switch (encryptionVersion) {
      case 0:
        return decryptPayloadV0(encryptedPayload, encryptionKey);
      default:
      case 1:
        return decryptPayloadV1(encryptedPayload, encryptionKey);
    }
  } catch {
    // If the encryption key is incorrect, it may throw an exception (during the UTF8 conversion).
    return null;
  }
}

export function getSupportedEncryptionVersions() {
  return {
    supported: [0, 1],
    recommended: 1,
    deprecated: [0], // Keep this one for unit-tests (as there is a constant key/iv)
  };
}

export function getRecommendedCryptoAlgorithmVersions() {
  return {
    compoundKey: getSupportedCompoundKeyVersions().recommended,
    hash: getSupportedHashVersions().recommended,
    encryption: getSupportedEncryptionVersions().recommended,
    verificationCode: getSupportedVerificationCodeVersions().recommended,
  };
}

/* eslint-disable no-bitwise */
export function getCryptoAlgorithmVersion(compoundKeyVersion: number, hashVersion: number, encryptionVersion: number, verificationCodeVersion: number) {
  return ((compoundKeyVersion & 0xff) << 24) | ((hashVersion & 0xff) << 16) | ((encryptionVersion & 0xff) << 8) | (verificationCodeVersion & 0xff);
}

export function getCompoundKeyVersion(cryptoAlgorithmVersion: number) {
  return ((cryptoAlgorithmVersion & 0xff000000) >> 24);
}

export function getHashVersion(cryptoAlgorithmVersion: number) {
  return ((cryptoAlgorithmVersion & 0x00ff0000) >> 16);
}

export function getEncryptionVersion(cryptoAlgorithmVersion: number) {
  return ((cryptoAlgorithmVersion & 0x0000ff00) >> 8);
}

export function getVerificationCodeVersion(cryptoAlgorithmVersion: number) {
  return (cryptoAlgorithmVersion & 0x000000ff);
}
/* eslint-enable no-bitwise */

// TODO: Move these to the backend. We should not let the client generate a SALT or a URL.

export function generateSalt() {
  // Generate 6 characters of 8 bits, which will generate 8 characters of 6 bits
  const random = CryptoJS.lib.WordArray.random(6);
  return CryptoJS.enc.Base64.stringify(random);
}

export function generateUrl() {
  // Generate 5 characters of 8 bits, which will generate 8 characters of 5 bits
  const random = CryptoJS.lib.WordArray.random(5).toString();
  const bytes = [];
  for (let i = 0; i < random.length; i += 2) {
    bytes.push(parseInt(random.slice(i, i + 2), 16));
  }

  // This will be exactly 8 characters long in Base32 - 40 Bits dimension
  return Base32.encode(bytes);
}

export function generatePassword() {
  const base32Chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567'; // Base32 character set
    let password = '';
    for (let i = 0; i < 8; i += 1) {
        const randomIndex = Math.floor(Math.random() * base32Chars.length);
        password += base32Chars[randomIndex];
    }
    return password;
}

export function getLogoBase64() {
  return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAPwAAABACAYAAAA+oxppAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAALiIAAC4iAari3ZIAABqJSURBVHhe7Z0JnBXFnce7B1BRkQRmQEaOMByiBBW8iEQTj2xY1qxXUKMmIUY8okFFVIIHh4qg4pmoEeMRdFU0MWo8WFdXOVTiEQMq4iAgwgzMCCJsQGbgdX6/qq73qutVv9fvmHE/H+vL50d317+6uqe7q6v6X8fzvTLS+e/bu2IxCBoSeN63sewNVUF7QN+ANkCboAZoOQ6+GMu3offWD25Lm8PhaEFKzvCd39mBzBycgdXjoAMZxPACWQcthP6MU3ps/ZA2X4pQh8NRVkrK8J3fbG6PFOZg9XAZUhaeWH9Qu5HhusPhKCMlZfjKvzV/D4tX5FbZ2AEN/OyQdkvlpsPhKBcV4TInlQubdql8o2kQlp3CIEkQ7AZxmUvN0BfQemgTtB2yxdO1c3gEAY7dFRrY+c2mtmGQw+EogpwZvuq1psrK15rGeCnvLS/wFmG5CNvfD80etgOIS10boQeg82E9BtofGgjtGy4HQ8Nhvwh6FNoCmWkg10sqX28aia0PoEX+du91HP+XOC86AR0OR4FYq/RVC7b1h2k0Vn8K0fOuM79x2E7im71qQdO/Y/Ec1zVegJ3hiUAaf8fiALklYJV+MNJYjPNASe/Tk99PWDKsgu5DVeC+xmE7fyqDHA5HPiIlfNW8bYdCD6E0fRfV6nFQV62ardS5av62dmKHbBsVeYlUztvWAWleznShCyrnN+0Umni8Csv+UiTw+MnQMcsWBD2hSbAvRhp3Q2wKdDgceRAZvsurXw7rMnfbs8hAr0Ono0rd3qhi65LVeJJtk9LwA+9+7DFNpBt4d1SkghmhiRnavj9F5Dr/RW0Z4WXgnQO93eXVbY/h73AZ3+HIQUWXV748HhlmnpcKRvipwIe8PAq4JEZ4WgqkvRe2h0dsqeB4hO9Oux5uKp/dUDuU+Cfj73gdaR8mdnY4HFlUoJS8GvK1UjOZiC2cUqS8dlm2lNcGkp8E2baMiC08t3aDrsSaw+GwwG/ob4ffxaaWQTOhJi0sI2ILpxQ2mxT+y2kXZmu4bOK7C6rTwnR9q8tLW+ULxeFwRKjwUdpCqBan1Yzv7ovaBMFArE+DGEe3CxFbOKWw2ZRKsDdXbA8uqwiCfbE+xbBR+OSQ+zscjiio0qNUjGqj1xzcV3/Mrk1Y39WwZURs4ZTCZlMq3o73kbfr2mN2/cLbEdyP7W2GXcrhcGRRwdxjyPd9bxcaLba0ctkVNptSCXaR44Xd83bBuq/Z0nI4HNnQaYcS0SJiC1citnBKYbMpEVu4ErGFKxFbuJLD4cjCVqWXIrZwpVx2hc2m1Bp2h8MRwValT1eJbeFKuewKm02pNewOhyPK/4cS3o+EK2XsdiWxOxyOCNYmN4rYwpVCe8oMh9K5zWJLSxA2oRnyuSRGeERJ7A6HI0ppJXwQLMH6JsP2hjSCaHhUoP6E3QOsv2nYPkW6HA2nh2Urid3hcETwq5/YbOaOz6B9637coRE2TkTJ4av6xBPvQ4Nhb+YG4vwIi0kQJ8fgAJzL6kZ22BLavoXFe9Bu3A6ph5j+Rm4gTiUWt0LfhdZAl8M2H0vamCaPtye3Q3h+AxGnAfYBWP8HlB6BBz6ADlDnVwrV1dVdsBgI8fg8RpI3CUcLPl9XV8eJOgsCx/sOFn0hXqO5SKOJ4cWAtHbFgtenO8T1JLSBFuG4HJKcE6TPtPtAHURAfpj2SqS9QG4mA8f5Nyz2gnjt/w96CmnkvLfYh3M2/CfEe/FXxH+J4XEgPgeRcU7GjhD3WYd90sO+YecErD+D2IOTf8dS2J/CMgLi8f7tA20XAclgffRz6GOk+aEISQCOxWvCe8A5JBNNZEP8vWZvsmb4NSfv0QhbbIaHPX3Rq2dt9r2dvZ3rTu4QmXwS+8dmeOwvMryievbm9th/a7gpwP6xGR77N8Aem+H18ysUXMz2WFwL/RwqZlLOI3Dz5oXrecHx+BDdB/GhUnD/E5EO/96CQHoXYjEG4qzBkeHKCZiOY44P17NA2pzW7DroEKjQLszMrMeH64nA8e7C4ly5JdgPacS+kBD/x1g8LrfSnIF9Hg7Xs8A+fMkys/E+kIcRnxOzCmDvj4U+5doc2IeH62kQ734sRsmtguHz+ib0G6Q9V4RYwDGY0adBfKEVPBFMae3wIXU/7RCYmV1g20/JwMzsAtt+SsQWrlQkuKB8Wz4CjYWKyezFwIdEz+yEk4xMlqvJwfnfgAVrTDVQoZk9J0ibE5uwtBwGFTNeoZjz+Wu4VPCFk4tLw6XO5Th3lZltsHap258OlwoWivrzvS1cmhRdIwO8nhzpOQfnypdpFghnwcfrz5dRUbM+VYg/xSZiC1dKgm0/pSTY9lMitnCl4mEJxOqdDmfVeRZ6LI9mQyxdElfncRNZe7pAbmVxOuw9wvW8IO5+WFwit9KwGsxqNB9iVkNziTMQW0tPpM0HcjqkZwxe6XchXhtberqehwqqzoe8Ba2Xq4IfhMsscI6sUfITzGRvyJw1SUdPczOU8UOVBjMnnws+EzbxnrAGrMNeruPkahaXQfxbdFZC/w3ZrnmW/O4Pf2FmD1GlX316x0bYYqv0sIsqc/eHNuH7JlBTYT23+oyOrzGcYP/YKj32T1fpuz/0Bb/TjoQ+9nz/4dWn7yFKe+wfW6XH/g2wx1bp1fkVCh4alo6sEiuegEahmvVPuVlecDz6QFSJwm8/Zgq9FJuIY08J13OCtFhTYLVSwWt/Evb/SG4WD9LeFwtea/Us8AdFTkPazOwtCo7NFxGfEbIWGojjZv1wCeKdhAXvlw3ewwfD9TTYh88mrxOfVTIf8SLTriMOXxaLINHlHDyNOGahwHi/x+JsuSUYgnjMP7FgH9Z6+PnF506xBNof+2Y+m2W8dyB9Orip0DWIl/h3HEqq0neftXEXLwiewnv+dugKaD7C/kNagW0/pRDE/yX2mwONh2Z6qeCPocm+nxKxhSsVj7qpigdxQVsks4fopTu/E38BNYotyZm42Umrb/qLj/wB515yZg+hc05/8dMZ1uKZPUR3uvHlP0SuZqG/KFlT0f/2uE+B/SGV2cnL4bIcmPcjC1xDFrh3QitEgIS1KNMRx7ToW1Kw4Ly+kMxOKiyzxwgRW7iStHsH+0FwhBbONvQzhRFo4VlSIP5Zhu34Hg9upJNED8tSEnuRmDvTg9oiICPzW02vTj6CG8gb/6jcFPSCTpareTHPXbSWlAnz+7vFrosF87cPjgmXacISUJ/tiC8j/UUxFHFsGfCocKkoZ4YvhHwZ17y3LIQKaQ0QlPYNH+Ctz9I0ahPTVwmi4VGBHg9s5PHbGzYfkqVsNDwqYgtXKh/FOJqS8itIpc8bSGchYdVQdwCdh4c1l9MpjpY898RNQWWAVe7lclXAzz8TltL8xFO8GkpBTzybEU2ODpdkNUSfRGtTzH3iPgXvV2rXWnacMW2hEWTbMsrY49PIDs8oib04zA+Cc5HZ9KpsWUCa9KKzCUnxHEp38VBjSb+F7p1mFTarGSgBpXiN81F0s2eh4HqwppL2DYFBuH5m5j0IUr4ivjxZpafDT10DOh2HylUJ0mAT12C5JViIY30RrrcmnJpdb/VIkpFTONfslq08+D3v/dzMHcJpt+qsbzbCFuu0g70Zdtu89HNgEw8n7LFOO8TZCDtLCf56bNa89LAvhj3WaQd7A+yxTjuen9wsDDwE/IZmm7gO22jZRhrpOxDCh5Hfiq+oDJsEHIcOl9/ILcFw7E/nlAB2VjX1KukLsOec7x/78LcE7pFbAnqCWR3O+y0ZwoduAY6jZy4B0mZmeV1uCehcZI1Ef1Dj4H1mRpqFtIt6CeH4bLbUnW4RJxzsv8XifLnlvQ3bQQhjrYglNp9jQn9Mup0cdtPJdy7srF1FQLxinXaHIt7fwvVYsB+b2ei7UhldnH+4LkAc3kOeg/LSMx+wCZbXM8kLgvdgtt9z5gZ7hh/dqRG2+Aw/ulMz7PYMP7qTzPAzN8Rn+NGdNsIen+FHd1oMe3yGH92pAfb4DI/zk5uFgQvLm8rvP/PbLh/M+LdBV+Jm5XQb4hj8u1gCVYsAuX4g9oucM+IxU6nvUqbJB4illhXENzN8MdyAY1werqdB2maGLxTetxqkzWavgsHxWaLzOinHVbpzDGx8jvhCVs6838L2a67AxuvB60L44qb3W7x0YLsbi3O4DnjtD4CNz08ExCs2w9Pbz08J9vzji5F5jUueL2G+4jPAzK3CyHikzybQNEjbzPDFcHRpHW9s4ZTCZlMitnAlYgtXIrZwpSLBhabzhG3xbCbhQ5oUdl9lic2SOx+nQSqzkz/iuLYXFL23Cj4Q/Ob/WoLr8zEWLDwUhyETqILE/H7Xe6rpDj++NER7fJiBjuB6CJvCyvkDpqxus7mazaTsscl7fjpEByw/5Sg+Z3Tc6pmdtaab5Wr50bz0qBekJQv9aJgMV5L2zLYeR5EJ1+PIdWm32+z2qKSd6+ZSrpcCHq7N0MVYZdsznTq8YWcZYqnBMQQsdXRG42FiH3wr4YOmShXCl4r5CSHAObA7qP6Qj8T+7C6blBeh66GbEup26H+hJLBmxr+f3WzziV1BVfWzFPTMy+vAjkbkYEiNF2BNi+emYJVafeuydFXf8ezzLlqDQjh2gbXLcsGH1PYc0DHIlhh25uIgMS75SfgMdArOgX0bktRO2UrCa8ou4LZrrouFEJ+DFX6vO9dncqhEVOk/+VXnRthiq/SwN8NurdLDJqr0sMdW6RFnI+x8s1mr9LAvhj22Sg97A+yxVXqen9xsWZAB2SrBwT5szyV83bDDBc8rC8Q/EYs/yS3BJ9BDkM0LzwxyLKS3O1+HtK1z7yNts0p/JuLqHXGKBmmbVfrbkbbeQanFwTn8EIsX5JaAn0/XIZw1ofNkkHheD0K4eO3DZj5j9CP8DOHs7MJPMMVxCDe71AoQt5gqPZ/jkRC7+nJQjYI9Dtl0zY5D6leSm5BeXHddAdI2q/S12Ed/YSWi1PHwVilsNqXWsLcGuOiq66pOLieK2Y2W7exXQBywYupqyOxkMgo3/5vhej70F2G5SeKsKzfMuHq3ZeVn0b3vb+CepJ+AcF13Qh6C68f7ozfHsbTM61wrEL7A2e2V56h3UGIhSUdhJc6NNUkqZ2aPoQJ/h94RJxGlNsvZpZDb5jHkDDcZu13lsLce+jdYLLhBHHRia0MuBDYl/USufqW04mtVgozB2p3utGTzHL/D6RhT2Eaa6Z8CrHWy153+In0HabPLbrnZGenSJ8SRbRz1p+BzMB/nfqDcLAo+5AU/6Pmcdrbff+dh5IFsNvnTj5LAW4etVZqN+mjlmErRvIUlY9uOIUvIOBuXJO78qNaF46V14kp41Wyk4LcVP3/4QMRpBMTqqt6rin0DvooSVuer+o3+/wmXhKMZL4JUZy9+q9taMejBV92jWY1mdV7/GXS9+bOciHyCTM82czpc9WZY+iBexn3kPU6C+UzRZ1FwZ6wKPwg2QZ4mhokSC8ttUHMYrtTOTwXiQFj/p2FDdTrYr/ctjcIDvfLCyq1eEJyN8KXQl9A72E5XaXvf3DgIYX3FfhntgEQVB8u2oXR7QIV2nqsfhmfsrVjC44bxF2v1wRY8eJZzCvH4vaWPBWd171o8CBxb/UwOcTINNh/pDyWPmRmz0DqwpNIv7LH4m9jc1Nqw95w6Dz6nJ0AqM9D5tUKuZsD147XWm9u4j/7CbJXutDgPOi/pqVfdaPnS/AuuY9xoSR36BPSXPvPYGOwbV7hY8XvPaOQFYjVHwbfkfisuqVoGG2ej4YWqoiGEpfNA2OtgpzONTjU613QWQicyDjdqbmpsj1dIF39HULf80i7CmYZ96XDjkD3T8bAM4vG3Ig69sBwhpL/J2LllEOxbYD8U6+ZQxjdhs44nTgIu4KlY0LGWy6PMB44PG/9+HivTnVg+cINwcyMDbpAuveD60FXhcArX84L99VF15CXsH+lTjjim087akaQYkDb/RrZI6M8K7yXvNb3NrFfleviYwTgS7Q65WRw4DzrO6LwSzWsG9yB9vQUkDfa7BQvWBkys90sH+xbbDj8U8Xh9IiAeP0M4bFZ3Rs9A3LhhsQLs9wAWbDHSoVOcTYosJPNmfn5ff258/7aHetKIAnMD1ldrNuobkHCSIGOtxfoTmk3pUOiNmhsbRIm2fFzV1hVjqz5JZ/YbG0bBvgDqH8bXNYuZnfGw/n2ojWajVjCzh/Zeho1aJ2zFw5cIvat8E8eJHT7YrkqHjJ7ZyWTz4cGN4guTbbIKOol48wqB3l3d83800tW9vy0K/iY6JyfKrTTMxJw8gteC18R2rZTY/pw16KVQcB4sHefJrSz0vvMm5gAcxWvm/WppcDz6GehH0JtcL8H9fBzSW7RM+AloPt9sSePzmu/6C3Fe+pXmnO4QH3pv5biqFNYXGjZK9Vzi+mRojWZT6oFy8Mma6Q1/gY6EekMjam5oeAm2+6FOWlylRX5KdjqoufGzNtj+hWGnxHx3BOsHGjaqXMNBC4Wl30jczKwx14Bvfb1N9lHE4/x9iUF8VudYtdcxZ3cx+/yb2yWBc2D3T9Z++OLB1f7K4HUwa2Cs+elNdiastpu96FhN1p1pcbDkVKU7Uc1pJmarSKwzF9eSzyk/iTh5hYKdcV5Dprc2t4X70OnLcRaqb0FB+DXT1l2FpTnBwivLx3cV3uSaaQ3DcW9Zupgchziiiok4wxCH7fG5HDk8wVzNCMgA/jHLx3cRE/nhvDiPmXkzUG30D0YcVvMZh9Uls/p+Gs5LjTorGFxspkfvadKhh+wqyhuxGDfE2vaPNNmWzskk+YDxIXgWcdkBoyCQDsek823OTxw+hCyZHkda4uGHnZ9JfIh4HGZ29u9nda+s4DhMmyULH0xO/JgEnjPbjsviIMM5sISkI4zON15/Volz9pTDPuxpx5ckrxOb936f5HywH59r9rvn382/Yzn20zOqAPFY26F/RT07nMMv5+xH2Id5gveULxS+RLn9AfbTnZNZYD8OvmLHMP79sS8WE79m6roj8eSYTgs+QEM+ntD1/Zqpa/HW8t9CHP4hOvxu+w7iyO/0qetYK/gvxOOJFAT+Sg5wOHX5hK7ihvWZuo4PE0vyyMOEeC8ijpj5BHHY0YUeWb0U24o4gxCH3TAdDocBq/RvISetEu+WtIKd8C0squ3LJ+zZhDjTxfdxJI7XE2Gz+1y7VkxTjEy2sCJAaRt490ByyGskvratvrcDbxs0HfsNS2f2a9dWw/Y4wjum44fCeWT6qaeCMxHW1ojztsvsDkc8rBZ6fa5Zqw8tVLCqOvjjq/YUGQhxWGW3Dc+kA+UkxEtPy4S4dOqx+yfbkMUxDFj1ZffS67Bf2nHRZ8raPohNz71tIsJ7EFd4YJE+PcWsFZjVyQsQ53fhusPhMBB1fz/l3QeZPxvVAUp/22P9XKgutOk6HJrbd3J9uucSMt0b0LEocYeiVL4T9lroc+h9bN+E8CGw/wRKZ3bsfzRscxFnIGQeg868tIMK65OgjqFNqQHi+G+HwxFDuvTtO7GeJS49sCanLJvcjdMve30n1X8XmZWeUFvTAZvKrtheUXH7yoldkQUz9JtYv0vge3sgQ35eO6VbxLHVZ2J9e5wEeyBNgPT2dsVanOWRyyZ1E848nCeb+p7kusH1OE+m4XA4Yshk+KvrByMz0+ttdtn8DJEOq72mWy03EO+HiPdnrEZ/vkilxGY8z5uC+OYougj9J9W1DXb4J+HT+yrsK6vw/A6P0gDbiGVTuonhjv2uqu+DKAsQ1tWIyxF4Byy7plvBP+/kcHydSGd40u/KOo6vNdt2CYccHlV7bbXoA4947CnEET96DzwTvjxuxT76DKxev0l1vrddtEvTZ2B6/nXoOzgB+4vx5jgmR4ixCUWfg0xxNuLNDNcdDkcM0fY7fBtD74pOklENhv7Ud0K9qMojc81FCXs4wt7S4pg6FHqk34S66MiuJm8swu+GBoXxbHoROlxldhx3D2w/CfE8zLjPuMzucCQjkuFrp1ZvQUYe5QXBZtmMFtFRFanU0/3HrxEjw2qvq16KzHYE4t8q7KKZzSp9wAi3TzDsUnJ/NtNdHXxZMRznwmq6h+N1w3HnwPa9SHwpNifqPzTocDhykNVDp/b66n/4Ke/noec7ImSuo6CX+1+2RkwRXDuteiviXwzbEX7gzTfjh9J/F4zbGwy7VOA9j+VQpHfNspv3RAgy+2Vr2K7P2sTQrPgpbwt0KuKLjj8OhyM/1i55H03fi9Xns0Spm63BKFkX9L90NUdvCRB/HsQq/onQq4gjO96kgg3Yjvb/TqFGkAqaQnsztp+DfoD9R0DpHwFA+mNxHKbVN4yri/udivilzKLqcHztiDjtTPYet5q92e7Faly822C6eumMvfjDgmn6j1vNSQXZPXbBRzd1zxrMAju7xbLd/m3YOewwzd6XiH05lFSfUVRnC87m1KU3deekfw6HowByZniy99jVHPfLUVJxA2NqURJfufSWHqKtvlj2vvjTXjid8TgjzggbN8qLI8xOWXpzd3MOOYfDkYC8GZ4gM+6Hkn4WVtW0wDbmIrEb/HZtnl9yY7X4Bk/CgAs/7R34oomOGT3XyCu2v5+BFwtnL3E4HEWQKMOTAWM+3R2xb0TGz+cVZyeZB1HqP/3hHT05BXMWA8as4rjhw3B4/qwTe/eZk0hE8b0ZfsqbsOSOHrlmoXE4HHlInOEVA369agQyPb+xOZG/HaYaiME3nDyQP2zAKYDZzMahsxyvzfnF9wnj5eI9xLkYL46cY4MdDkcyCs7wZMD5q9it9kLsPBZLznuXBE7KYOsrb4O/jjEDy1s//F3Pomb2cDgc2RSV4RX7nLeqG4pozrjJ7+/Yn1cqAM47fj9O67Yld/UsaAooh8ORn5IyvGKfcz6pQkqcRG8UqujZjj1VdVdLE1/MfMu54GYtubtXS/wggMPhAGXJ8Ip9Rn/S1vfFHO2nIGfzO12f0thkJQ7PecFmB4E3b8nMXs4h53C0MGXN8Dr7nrVidyTPySA5NTEnHGTm5xBbTtH7Ml4I73xwb+9WnR7Y4fh643n/AkbiIOMGEfPrAAAAAElFTkSuQmCC'
}
